import React, { useEffect, useState, useRef } from 'react';
import moment from 'moment';

import styles from './ArticleMetaData.module.scss';
import Head from 'next/head';
import { TravelJournalArticleProps } from '../types';

import ArticleInfoItem from '../article-info-item';
import SharingItems from '../sharing-items';
import { handleSendEmail } from 'utils/handleSendEmail';

const ArticleMetaData: React.FC<TravelJournalArticleProps> = ({ article }) => {
  const { title, subheading, author, read_time, date, header_image } = article;

  const formattedArticleDate = moment(date).format('D MMMM YYYY');

  const articleUrlRef = useRef('');
  articleUrlRef.current =
    typeof window !== 'undefined' ? window.location.href : '';

  const [isClient, setIsClient] = useState(false);

  // Empty useEffect hook that runs once after the initial render.
  // This is used to set the isClient state variable to true, indicating that the component is now being rendered on the client side.
  // This is necessary because certain values (like articleUrlRef.current) are only available on the client side.
  useEffect(() => {
    setIsClient(true);
  }, []);

  // If the component is not being rendered on the client side, or if articleUrlRef.current is not defined,
  // the component returns null. This prevents the component from being rendered until both conditions are met.
  // This is necessary to avoid a "Prop `href` did not match" warning, which can occur when a value changes between the server and client renders.
  if (!isClient || !articleUrlRef.current) {
    return null;
  }

  const message = encodeURIComponent(
    `Check out this article: ${articleUrlRef.current}`
  );

  return (
    <>
      <div className={styles.lineContent}>
        <div className={styles.articleInfoItems}>
          <ArticleInfoItem detail={formattedArticleDate} showEllipse={false} />
          <ArticleInfoItem detail={author} />
          <ArticleInfoItem detail={`${read_time} min read`} />
        </div>
        <SharingItems
          linkedinShareUrl={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
            articleUrlRef.current
          )}`}
          facebookShareUrl={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            articleUrlRef.current
          )}`}
          emailShareUrl={handleSendEmail(
            title,
            subheading,
            articleUrlRef.current
          )}
          whatsappShareUrl={`https://wa.me/?text=${message}`}
        />
      </div>
    </>
  );
};

export default ArticleMetaData;
